import { makeAutoObservable, when } from "mobx";

import { validateTeamAbout, validateTeamTitle } from "@/models/api/TeamApi";
import { validateName } from "@/store/user/userUtils";

import type { FormFieldModel } from "@/components/form/FormFieldModel";
import type { RootStore } from "@/store/RootStore";

export type UpdateUserFields = "name" | "username" | "about";

export class UpdateUserForm implements FormFieldModel<UpdateUserFields> {
  private rootStore: RootStore;

  formFields: FormFieldModel<UpdateUserFields>["formFields"] = {
    name: {
      value: "",
      focused: false,
      error: null,
      validate: (value) => validateName(value),
    },
    username: {
      value: "",
      focused: false,
      error: null,
      validate: (value) => validateTeamTitle(value),
    },
    about: {
      value: "",
      focused: false,
      error: null,
      validate: (value) => validateTeamAbout(value),
    },
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.formFields.name.value = this.rootStore.auth.currentUser?.name || "";
    this.formFields.username.value = this.rootStore.auth.currentUser?.username || "";
    makeAutoObservable(this);
    when(
      () => this.isReady,
      () => {
        this.formFields.about.value = this.rootStore.teams.personalTeam?.bio || "";
      },
    );
  }

  get isReady() {
    return Boolean(this.rootStore.teams.personalTeam);
  }

  async submit() {
    const { personalTeam } = this.rootStore.teams;
    if (!personalTeam) {
      throw new Error("UpdateUserForm submit error: personalTeam is undefined");
    }
    // The server syncs the personal team's title and permalink to the user's name and username.
    await personalTeam.update({
      title: this.formFields.name.value,
      newPermalink: this.formFields.username.value,
      bio: this.formFields.about.value,
    });
    await this.rootStore.auth.fetchUserData({ force: true });
  }
}
